import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BookOpenText,
  Eye,
  Filter,
  Loader2,
  Search,
  Sparkles,
  Users,
  Wand2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { track } from "../lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  getCommunityLesson,
  listCommunityLessons,
  importCommunityLesson,
} from "../lib/api";
import LessonDocument from "./LessonDocument";

/**
 * Community tab on the Teacher Dashboard. Browses lessons other
 * teachers have opted to share. Available to all paid tiers per the
 * Phase 92 spec — backend already enforces the gate, so we surface
 * a friendly fallback message if the API returns 403.
 *
 * Layout:
 *   1. Filter row — Grade · Subject · Skill · Type + a search bar
 *   2. Card grid (newest first)
 *   3. Preview Dialog (full lesson) with a "Use this lesson" CTA
 */
const ALL = "__all__";

const GRADE_OPTIONS = [
  "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8",
];
const SUBJECT_OPTIONS = [
  "Mathematics", "Language", "Science", "Social Studies", "Arts",
  "French", "Health & Physical Education",
];
const SKILL_OPTIONS = [
  "Critical Thinking & Problem Solving", "Innovation",
  "Self-Directed Learning", "Collaboration", "Communication",
  "Global Citizenship & Sustainability", "Digital Literacy",
];
const TYPE_OPTIONS = [
  { value: "lesson", label: "Single lesson" },
  { value: "unit", label: "Unit plan" },
  { value: "summative_task", label: "Summative task" },
  { value: "skill_only", label: "Skill-focused activity" },
];

const TYPE_BADGES = {
  lesson: { label: "Lesson", className: "bg-[#96ded1]/30 text-[#1c2d3d] border-[#96ded1]/60" },
  unit: { label: "Unit", className: "bg-[#1c2d3d]/10 text-[#1c2d3d] border-[#1c2d3d]/30" },
  summative_task: { label: "Summative", className: "bg-amber-100 text-amber-900 border-amber-300" },
};

const formatShared = (iso) => {
  if (!iso) return "";
  const days = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} wk ago`;
  return new Date(iso).toLocaleDateString();
};

const CommunityCard = ({ lesson, onPreview, onUse, busyId }) => {
  const typeKey = lesson.is_skill_only ? "skill_only" : lesson.output_type;
  const badge = TYPE_BADGES[lesson.output_type] || TYPE_BADGES.lesson;
  return (
    <div
      data-testid={`community-card-${lesson.id}`}
      className="rounded-xl border border-[#4a6984]/15 bg-white p-4 sm:p-5 flex flex-col gap-3 shadow-sm hover:border-[#96ded1]/70 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em]">
          {(lesson.division || lesson.grade) && (
            <span className="px-2 py-0.5 rounded-full bg-[#f4f5f7] text-[#4a6984]">
              {lesson.division || lesson.grade}
            </span>
          )}
          {lesson.subject && (
            <span className="px-2 py-0.5 rounded-full bg-[#f4f5f7] text-[#4a6984]">
              {lesson.subject}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full border ${badge.className}`}>
            {lesson.is_skill_only ? "Skill-focused" : badge.label}
          </span>
        </div>
        <span className="text-[10px] text-[#4a6984]/60 shrink-0">
          {formatShared(lesson.shared_at)}
        </span>
      </div>
      <h4 className="font-extrabold text-[#1c2d3d] text-base leading-snug">
        {lesson.activity_name}
      </h4>
      {lesson.tagline && (
        <p className="text-sm text-[#4a6984] font-medium italic -mt-1">
          {lesson.tagline}
        </p>
      )}
      {lesson.overview_preview && (
        <p className="text-xs text-[#4a6984]/80 leading-relaxed line-clamp-3">
          {lesson.overview_preview}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-[#4a6984]/70 font-medium">
        {lesson.skill_focus && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#96ded1]/15 text-[#1c2d3d]">
            <Sparkles className="h-2.5 w-2.5" />
            {lesson.skill_focus}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Users className="h-3 w-3" /> {lesson.display_name}
        </span>
        {lesson.used_by_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[#1c2d3d] font-bold">
            · used by {lesson.used_by_count} teacher{lesson.used_by_count === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="flex gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPreview(lesson)}
          data-testid={`community-preview-${lesson.id}`}
          className="border-[#4a6984]/20 text-[#4a6984] flex-1"
        >
          <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
        </Button>
        <Button
          size="sm"
          onClick={() => onUse(lesson)}
          disabled={busyId === lesson.id}
          data-testid={`community-use-${lesson.id}`}
          className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white flex-1"
        >
          {busyId === lesson.id ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Importing…
            </>
          ) : (
            <>
              <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Use this lesson
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const fieldClass =
  "bg-white border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-10";

const CommunityTab = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    grade: "", subject: "", skill: "", type: "", q: "",
  });
  const [pendingQ, setPendingQ] = useState("");
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const queryKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCommunityLessons(filters)
      .then((res) => {
        if (cancelled) return;
        setLessons(res?.lessons || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || "Could not load community feed");
        setLessons([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  // Apply pending search keyword on submit so we don't fire on every keystroke.
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters((f) => ({ ...f, q: pendingQ.trim() }));
  };

  const setFilterField = (key) => (val) => {
    setFilters((f) => ({ ...f, [key]: val === ALL ? "" : val }));
  };

  const handlePreview = async (lesson) => {
    setPreviewId(lesson.id);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const data = await getCommunityLesson(lesson.id);
      setPreviewData(data);
    } catch (e) {
      toast.error("Could not load preview", {
        description: e?.response?.data?.detail || e?.message || "Try again.",
      });
      setPreviewId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUse = async (lesson) => {
    setBusyId(lesson.id);
    try {
      const res = await importCommunityLesson(lesson.id);
      if (res?.already_owned) {
        toast.info("That's your own lesson — opening it in My Library.");
      } else {
        track("community_lesson_imported", {
          original_lesson_id: lesson.id,
          grade: lesson.grade || "",
          subject: lesson.subject || "",
        });
        toast.success("Lesson imported to My Library.", {
          description: 'Look for the "From Community" badge on the new copy.',
        });
        // Optimistic count bump so the card updates without a refetch.
        setLessons((rows) =>
          rows.map((r) =>
            r.id === lesson.id
              ? { ...r, used_by_count: (r.used_by_count || 0) + 1 }
              : r
          )
        );
      }
      // Take them to the library so the import is visible immediately.
      navigate(`/dashboard?tab=library&activity=${res.activity_id}`);
    } catch (e) {
      toast.error("Could not import lesson", {
        description: e?.response?.data?.detail || e?.message || "Try again.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const hasAnyFilter =
    filters.grade || filters.subject || filters.skill || filters.type || filters.q;
  const clearAll = () => {
    setFilters({ grade: "", subject: "", skill: "", type: "", q: "" });
    setPendingQ("");
  };

  return (
    <div data-testid="community-tab" className="space-y-5">
      <div className="rounded-xl border border-[#4a6984]/10 bg-[#f4f5f7] p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-4">
          <BookOpenText className="h-5 w-5 text-[#1c2d3d] shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-extrabold text-[#1c2d3d] text-base sm:text-lg leading-tight">
              Lessons other teachers are sharing
            </h3>
            <p className="text-sm text-[#4a6984] font-medium mt-1">
              Browse, preview, and import a copy into your own library —
              fully editable, schedulable, and trackable. Every shared
              lesson is opt-in and anonymous unless the teacher chose to
              show a display name.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSearchSubmit}
          className="flex flex-col sm:flex-row gap-2 mb-3"
          data-testid="community-search-form"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4a6984]/60" />
            <Input
              data-testid="community-search-input"
              value={pendingQ}
              onChange={(e) => setPendingQ(e.target.value)}
              placeholder="Search by title, topic, or keyword (e.g. fractions, persuasive writing)"
              className={`${fieldClass} pl-10`}
            />
          </div>
          <Button
            type="submit"
            data-testid="community-search-submit"
            className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white"
          >
            <Search className="h-4 w-4 mr-1.5" /> Search
          </Button>
        </form>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Select value={filters.grade || ALL} onValueChange={setFilterField("grade")}>
            <SelectTrigger data-testid="community-filter-grade" className={fieldClass}>
              <SelectValue placeholder="Grade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All grades</SelectItem>
              {GRADE_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.subject || ALL} onValueChange={setFilterField("subject")}>
            <SelectTrigger data-testid="community-filter-subject" className={fieldClass}>
              <SelectValue placeholder="Subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All subjects</SelectItem>
              {SUBJECT_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.skill || ALL} onValueChange={setFilterField("skill")}>
            <SelectTrigger data-testid="community-filter-skill" className={fieldClass}>
              <SelectValue placeholder="Skill" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All skills</SelectItem>
              {SKILL_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.type || ALL} onValueChange={setFilterField("type")}>
            <SelectTrigger data-testid="community-filter-type" className={fieldClass}>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {TYPE_OPTIONS.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasAnyFilter && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="community-clear-filters"
            className="mt-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#4a6984]/70 hover:text-[#1c2d3d] inline-flex items-center gap-1"
          >
            <Filter className="h-3 w-3" /> Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#4a6984]/70 text-sm" data-testid="community-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading shared lessons…
        </div>
      ) : error ? (
        <div className="rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900" data-testid="community-error">
          {error}
        </div>
      ) : lessons.length === 0 ? (
        <div className="rounded-xl border border-[#4a6984]/15 bg-white p-8 text-center" data-testid="community-empty">
          <p className="font-extrabold text-[#1c2d3d]">
            {hasAnyFilter ? "No matches yet" : "No lessons shared yet"}
          </p>
          <p className="text-sm text-[#4a6984] mt-2 max-w-md mx-auto font-medium">
            {hasAnyFilter
              ? "Try clearing a filter or two — the community is still growing."
              : "When teachers tick the share box on the generator, their lesson will appear here."}
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
          data-testid="community-grid"
        >
          {lessons.map((l) => (
            <CommunityCard
              key={l.id}
              lesson={l}
              onPreview={handlePreview}
              onUse={handleUse}
              busyId={busyId}
            />
          ))}
        </div>
      )}

      <Dialog
        open={!!previewId}
        onOpenChange={(o) => {
          if (!o) {
            setPreviewId(null);
            setPreviewData(null);
          }
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="community-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {previewData?.activity_name || "Community lesson"}
            </DialogTitle>
            <DialogDescription>
              Shared by {previewData?.display_name || "Anonymous teacher"} ·{" "}
              {formatShared(previewData?.shared_at)}
              {previewData?.used_by_count > 0 && (
                <> · used by {previewData.used_by_count} teacher
                  {previewData.used_by_count === 1 ? "" : "s"}</>
              )}
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center gap-2 text-[#4a6984]/70 text-sm py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
            </div>
          ) : previewData ? (
            <LessonDocument activity={previewData} enableRefine={false} />
          ) : null}
          {previewData && (
            <div className="flex justify-end pt-2 border-t border-[#4a6984]/10">
              <Button
                onClick={() => handleUse({ id: previewId })}
                disabled={busyId === previewId}
                data-testid="community-preview-use"
                className="bg-[#1c2d3d] hover:bg-[#0f1a24] text-white"
              >
                {busyId === previewId ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" /> Use this lesson</>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityTab;
